// @fastn/core — the product-analytics contract.
//
// One place that defines what may be sent to PostHog, from anywhere: the
// dashboard, the embed, and every service. Two things live here and nothing
// else does — the typed event catalogue, and the property envelope every event
// carries.
//
// Why a discriminated union rather than `capture(name, props)`:
//   A typo in an event name is otherwise a silent second event that halves a
//   funnel, and nobody notices for a quarter. Here it is a compile error. The
//   same goes for a renamed property: the funnel that depended on it fails to
//   build rather than quietly returning zero.
//
// Naming rules, all enforced by tests/unit/analytics-catalogue.test.ts:
//   - object_verb, snake_case, verb in the past tense
//   - objects come from the domain (connector, connection, widget, workflow,
//     tenant, agent) — never an analytics-only noun
//   - never prefixed by surface. `dashboard_connector_connected` and
//     `embed_connector_connected` split one funnel into two; `surface` is a
//     property. The hub_* events are the deliberate exception: a different
//     population doing a different thing.
//   - never `$`-prefixed — reserved by PostHog
//   - durations end _ms, counts end _count, booleans start is_
import { lookupError } from "./errors/registry.js";
/**
 * Codes whose bucket is not implied by their HTTP status. QUOTA_EXCEEDED and
 * PAYMENT_REQUIRED both share 429/402 with ordinary throttling, and conflating
 * them would hide the entire monetization signal inside `rate_limit`.
 */
const CODE_OVERRIDES = {
    QUOTA_EXCEEDED: "quota",
    PAYMENT_REQUIRED: "quota",
    AUTH_INVALID: "auth",
    AUTH_EXPIRED: "auth",
};
const STATUS_BUCKETS = [
    [401, "auth"],
    [402, "quota"],
    [403, "permission"],
    [404, "not_found"],
    [405, "validation"],
    [409, "conflict"],
    [413, "validation"],
    [422, "validation"],
    [429, "rate_limit"],
    [502, "upstream"],
    [503, "upstream"],
    [504, "timeout"],
];
/**
 * Map a structured error code onto the analytics taxonomy.
 *
 * ONE implementation, shared by the client and every service, so a
 * client-reported `error_shown` and a server-reported `connect_failed` can be
 * joined on failure_category without translating between two vocabularies.
 *
 * Total by construction: an unrecognised code resolves through the registry's
 * own fallback entry (status 500) and lands on `internal` rather than throwing,
 * so a service that invents a code tomorrow still charts.
 */
export function failureCategory(code) {
    if (!code)
        return "unknown";
    const override = CODE_OVERRIDES[code];
    if (override)
        return override;
    const { status } = lookupError(code);
    for (const [code_, bucket] of STATUS_BUCKETS) {
        if (status === code_)
            return bucket;
    }
    if (status >= 400 && status < 500)
        return "validation";
    if (status >= 500)
        return "internal";
    return "unknown";
}
/** Email domains whose users are fastn staff rather than customers. */
const INTERNAL_EMAIL_DOMAINS = new Set(["fastn.ai"]);
/**
 * The single definition of "this is us, not a customer".
 *
 * Belt and braces: PostHog also carries a project-level filter on this
 * property AND on the email domain, because a new surface that forgets to set
 * the envelope would otherwise silently readmit staff traffic.
 */
export function isInternalEmail(email) {
    if (!email)
        return false;
    const at = email.lastIndexOf("@");
    if (at < 0)
        return false;
    return INTERNAL_EMAIL_DOMAINS.has(email.slice(at + 1).toLowerCase());
}
/**
 * Runtime list of every event name, for the tests that check each one has a
 * call site and that no raw string escapes the union. Kept in sync with the
 * type by analytics-catalogue.test.ts, which fails if the two diverge.
 */
export const ANALYTICS_EVENTS = [
    "onboarding_started",
    "systems_detected",
    "onboarding_step_completed",
    "widget_created",
    "embed_snippet_copied",
    "onboarding_completed",
    "onboarding_abandoned",
    "onboarding_skipped",
    "connect_started",
    "connect_redirected",
    "connect_succeeded",
    "connect_failed",
    "connector_requested",
    "connection_auth_expired",
    "connection_disconnected",
    "connection_reauthorised",
    "hub_opened",
    "hub_connect_succeeded",
    "hub_connect_failed",
    "hub_integration_activated",
    "hub_search_zero_results",
    "hub_session_expired",
    "workflow_created",
    "workflow_tested",
    "workflow_published",
    "workflow_execution_succeeded",
    "workflow_execution_failed",
    "trigger_bound",
    "execution_detail_opened",
    "agent_thread_started",
    "agent_proposal_shown",
    "agent_proposal_resolved",
    "agent_feedback_submitted",
    "agent_run_blocked",
    "quota_threshold_reached",
    "quota_exceeded",
    "upgrade_prompt_shown",
    "plan_change_requested",
    "plan_change_reviewed",
    "plan_change_cancelled",
    "entitlement_blocked",
    "user_logged_in",
    "user_session_resumed",
    "user_signed_up",
    "user_invited",
    "invite_accepted",
    "settings_tab_viewed",
    "settings_save_failed",
    "api_key_created",
    "api_key_revoked",
    "secret_created",
    "activity_tab_viewed",
    "activity_filter_applied",
    "mcp_connect_opened",
    "mcp_url_copied",
    "mcp_client_expanded",
    "mcp_install_link_opened",
    "embed_method_viewed",
    "embed_install_detected",
    "ui_crashed",
    "chunk_load_failed",
    "error_shown",
    "permission_denied_shown",
    "empty_state_viewed",
    "search_performed",
    "retry_clicked",
    "bug_report_submitted",
];
//# sourceMappingURL=analytics.js.map